import type { Action, DevToolsEnhancerOptions } from '@reduxjs/toolkit';
import {
  appendLegacyResponseContent,
  applyChatProgress,
  applyHtmlSnapshot,
  setIncrementalResponse,
} from '../aiChatbot/slice';

export const DEVTOOLS_MAX_AGE = 25;
export const DEVTOOLS_MAX_STRING_LENGTH = 256;

/**
 * SignalR streaming dispatches these many times per second with cumulative
 * HTML. Recording them makes the Redux DevTools extension serialize the full
 * store per frame, which blocks the main thread for minutes once DevTools opens.
 */
export const DEVTOOLS_IGNORED_ACTION_TYPES: ReadonlySet<string> = new Set([
  applyHtmlSnapshot.type,
  appendLegacyResponseContent.type,
  applyChatProgress.type,
  setIncrementalResponse.type,
]);

export const truncateLongStrings = (
  value: unknown,
  maxLength = DEVTOOLS_MAX_STRING_LENGTH
): unknown => {
  if (typeof value === 'string') {
    return value.length > maxLength
      ? `${value.slice(0, maxLength)}… [${value.length} chars]`
      : value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => truncateLongStrings(item, maxLength));
  }
  if (value && typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value)) {
      result[key] = truncateLongStrings(item, maxLength);
    }
    return result;
  }
  return value;
};

export const sanitizeDevToolsState = <S>(state: S): S => {
  const record = state as unknown as Record<string, unknown> | undefined;
  if (!record || typeof record !== 'object' || !record['aiChatbot']) {
    return state;
  }
  return {
    ...record,
    aiChatbot: truncateLongStrings(record['aiChatbot']),
  } as S;
};

export const sanitizeDevToolsAction = <A extends Action>(action: A): A =>
  action.type.startsWith('aiChatbot/')
    ? (truncateLongStrings(action) as A)
    : action;

export const shouldRecordDevToolsAction = (_state: unknown, action: Action) =>
  !DEVTOOLS_IGNORED_ACTION_TYPES.has(action.type);

export const devToolsOptions: DevToolsEnhancerOptions = {
  maxAge: DEVTOOLS_MAX_AGE,
  autoPause: true,
  predicate: shouldRecordDevToolsAction,
  stateSanitizer: sanitizeDevToolsState,
  actionSanitizer: sanitizeDevToolsAction,
};
